import { useMemo, useEffect } from "react";
import { Outlet, Navigate } from "react-router-dom";

import { styled, useTheme, Theme } from "@mui/material/styles";
import {
  AppBar,
  Box,
  Container,
  CssBaseline,
  Toolbar,
  useMediaQuery,
  Grid,
  Typography,
} from "@mui/material";
import { IconChevronRight } from "@tabler/icons";

import Breadcrumb from "components/breadcrumb";
import Breadcrumbs from "components/extended/Breadcrumbs";
import { authenticateUser } from "features/components/helperFunctions";
import Header from "./header";
import Sidebar from "./sidebar";
import ChatBot from "./chatBot";
import navigation from "menuItems";
import useConfig from "hooks/useConfig";
import { notificationTickerStyles } from "./index.styles";

import { TAWK_PROPERTY_ID, TAWK_TO_API_KEY } from "utils/constants";
import { drawerWidth } from "store/constant";
import { openDrawer } from "store/slices/menu";
import { useDispatch, useSelector } from "store";
import { setPusherSubscription } from "store/slices/Main";

interface MainStyleProps {
  theme: Theme;
  open: boolean;
}

// styles
const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" })(
  ({ theme, open }: MainStyleProps) => ({
    ...theme.typography.mainContent,
    ...(!open && {
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
      transition: theme.transitions.create("margin", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.shorter,
      }),
      [theme.breakpoints.up("md")]: {
        marginLeft: -(drawerWidth - 20),
        width: `calc(100% - ${drawerWidth}px)`,
      },
      [theme.breakpoints.down("md")]: {
        marginLeft: "20px",
        width: `calc(100% - ${drawerWidth}px)`,
        padding: "16px",
      },
      [theme.breakpoints.down("sm")]: {
        marginLeft: "10px",
        width: `calc(100% - ${drawerWidth}px)`,
        padding: "16px",
        marginRight: "10px",
      },
    }),
    ...(open && {
      transition: theme.transitions.create("margin", {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.shorter,
      }),
      marginLeft: 0,
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
      width: `calc(100% - ${drawerWidth}px)`,
      [theme.breakpoints.down("md")]: {
        marginLeft: "20px",
      },
      [theme.breakpoints.down("sm")]: {
        marginLeft: "10px",
      },
    }),
  })
);

// ==============================|| MAIN LAYOUT ||============================== //

const MainLayout = () => {
  const theme = useTheme();
  const matchDownMd = useMediaQuery(theme.breakpoints.down("lg"));

  const dispatch = useDispatch();
  const { firebaseDetails: { ticker } } = useSelector(
    (reduxState) => reduxState.firebaseDetails
  );
  const { drawerOpen } = useSelector((state) => state.menu);
  const { container } = useConfig();

  //======================================= useEffect Hooks =======================================//

  useEffect(() => {
    dispatch(openDrawer(!matchDownMd));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchDownMd]);

  useEffect(() => {
    // subscribe pusher channel on component mount
    dispatch(setPusherSubscription(true));

    return () => {
      // un-subscribe pusher channel on component unmount
      dispatch(setPusherSubscription(false));
    };
  }, []);

  const header = useMemo(
    () => (
      <Toolbar>
        <Header />
      </Toolbar>
    ),
    []
  );

  const displayBreadCrumb = () => {
    const path = window.location.pathname;
    if (
      path &&
      path.split("/")[1] == "settings" &&
      path.split("/").length !== 2
    ) {
      return true;
    }
    return false;
  };

  return authenticateUser() ? (
    localStorage.getItem("businessInfo") ? (
      <>
        {/* notification ticker box */}
        {ticker?.visibility &&
          ticker?.message && (
            <Box
              sx={{
                ...notificationTickerStyles,
                zIndex: 1200,
                background: ticker?.bodyColor,
              }}
              dangerouslySetInnerHTML={{
                __html: ticker?.message,
              }}
            />
          )}

        <Box
          sx={{
            display: "flex",
            mt:
              ticker?.visibility &&
              ticker?.message
                ? "35px"
                : "unset",
          }}
        >
          <CssBaseline />

          {/* header */}
          <AppBar
            enableColorOnDark
            position="fixed"
            color="inherit"
            elevation={0}
            sx={{
              bgcolor: theme.palette.background.default,
              transition: drawerOpen
                ? theme.transitions.create("width")
                : "none",
              top: "unset",
            }}
          >
            {header}
          </AppBar>

          <Sidebar />

          {/* main content */}
          <Main theme={theme} open={drawerOpen}>
            {/* breadcrumb */}
            {container && (
              <Container maxWidth="lg">
                <Breadcrumbs
                  separator={IconChevronRight}
                  navigation={navigation}
                  icon
                  title
                  rightAlign
                />
                <Outlet />
              </Container>
            )}
            {!container && (
              <>
                {/* <Breadcrumbs
              separator={IconChevronRight}
              navigation={navigation}
              icon
              title
              rightAlign
            /> */}
                {displayBreadCrumb() && <Breadcrumb />}
                <Outlet />
              </>
            )}
          </Main>
        </Box>
        {/* <Grid container>
        <Grid item xs={12} sx={{ display: "flex", padding: "20px" }}>
          <Grid item xs={11}>
            <ChatBot />
          </Grid>
          <Grid item xs={1}></Grid>
        </Grid>
      </Grid> */}
      </>
    ) : (
      <Navigate to="/business" />
    )
  ) : (
    <Navigate to="/" />
  );
};

export default MainLayout;
